import React, { Suspense, useState, useCallback, useEffect, useRef } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Menu } from 'primereact/menu';
import DrawerV6 from '../../Common/Drawer/DrawerV6/DrawerV6';
import LoadingIndicator from '../../Common/LoadingIndicator/LoadingIndicator';
import Notification from '../../../components/Common/Notification';
import AllLocationsForm from './AllLocationsForms';
import LocationAttributesForm from './LocationAttributesForm';
import LocationNameForm from './LocationNameForm';
import MultiSelectBox from './../../Common/MultiSelectBox/MultiSelectBox';
import { useLoadBulkPushdownFilters, useBulkPushdown } from '../../../apis/bulkPushdown';
import { IdName } from '../../../apis/bulkPushdown/bulkPushdown.types';
import './BulkPushdownDrawer.scss';

const FORMS = {
	AllLocations: {
		Id: 'AllLocations',
		Name: 'All Locations',
		Message: 'Push task(s) to all locations which have this baseline.',
	},
	LocationName: {
		Id: 'LocationName',
		Name: 'Customize by Location Name',
		Message: 'Push task(s) to all locations listed below',
	},
	LocationAttributes: {
		Id: 'LocationAttributes',
		Name: 'Customize by Location Attributes',
		Message: 'Push task(s) to all locations listed below',
	},
} as const;

type FormId = typeof FORMS[keyof typeof FORMS]['Id'];

// Error Fallback Component
const ErrorFallback: React.FC<{ error: unknown; resetErrorBoundary: () => void }> = ({
	error,
	resetErrorBoundary,
}) => (
	<div className="alert alert-danger m-3">
		<h5>Something went wrong loading bulk pushdown:</h5>
		<p>{error instanceof Error ? error.message : String(error)}</p>
		<button className="btn btn-secondary" onClick={resetErrorBoundary}>
			Try again
		</button>
	</div>
);

// Loading Component for Suspense
const LoadingComponent: React.FC = () => (
	<LoadingIndicator isLoading={true}>
		<div>Loading...</div>
	</LoadingIndicator>
);

interface CheckedTask {
	id: number;
}

interface ComponentProps {
	baselineId: string;
	projectId?: number;
	checkedTasks: CheckedTask[];
	onComplete: () => void;
	onCancel: () => void;
}

const BulkPushdownDrawerContent: React.FC<ComponentProps> = ({ baselineId, projectId, checkedTasks, onComplete, onCancel }) => {
	const [selectedLocations, setSelectedLocations] = useState<IdName[]>([]);
	const [currentForm, setCurrentForm] = useState<FormId>(FORMS.AllLocations.Id);
	const [isFormValid, setIsFormValid] = useState<boolean>(true);
	const [formProjectId, setFormProjectId] = useState<string>(projectId ? projectId.toString() : '');
	const menuRef = useRef<Menu>(null);

	// isLoading (not isFetching): the LoadingIndicator below unmounts its children, so a background
	// refetch (e.g. on window refocus) would otherwise reset the active form's search and selection.
	const { data: filtersData, isLoading: isLoadingFilters, error: filtersError } = useLoadBulkPushdownFilters(baselineId);

	// When the filters fail to load, isProjectsActive / isProjectIdRequiredForLocationTask both fall
	// back to false, which would silently skip the project requirement. Block the pushdown instead.
	const hasFiltersError = !!filtersError || filtersData?.isError === true;

	const isProjectsActive = filtersData?.isProjectsActive ?? false;
	const isProjectIdRequiredForLocationTask = filtersData?.isProjectIdRequiredForLocationTask ?? false;
	const projects = filtersData?.projects ?? [];

	useEffect(() => {
		if (!hasFiltersError) {
			return;
		}
		Notification(
			'error',
			'An Error Occurred',
			filtersData?.errors?.[0]?.message ?? filtersError?.message ?? 'Failed to load bulk pushdown settings'
		);
	}, [hasFiltersError, filtersData, filtersError]);

	const pushdownMutation = useBulkPushdown((result) => {
		if (result.isError) {
			Notification('error', 'An Error Occurred', result.errors?.[0]?.message ?? 'Failed to perform bulk pushdown');
			return;
		}
		Notification('success', 'Success', 'Bulk Pushdown completed successfully');
		onComplete();
	});

	const validForm = useCallback((): boolean => {
		if (hasFiltersError) {
			Notification('error', 'An Error Occurred', 'Pushdown settings could not be loaded. Please close the drawer and try again.');
			return false;
		}
		if (isProjectsActive === true && isProjectIdRequiredForLocationTask === true && !formProjectId) {
			setIsFormValid(false);
			return false;
		}
		return true;
	}, [hasFiltersError, isProjectsActive, isProjectIdRequiredForLocationTask, formProjectId]);

	const onPushdown = useCallback(() => {
		if (!validForm()) {
			return;
		}

		pushdownMutation.mutate(
			{
				baselineTaskIds: checkedTasks.map((x) => x.id),
				locationIds: selectedLocations.map((x) => x.id),
				projectId: formProjectId,
			},
			{
				onError: (error) => {
					Notification('error', 'An Error Occurred', error.message);
				},
			}
		);
	}, [validForm, checkedTasks, selectedLocations, formProjectId, pushdownMutation]);

	const baselineTaskIds = checkedTasks.map((x) => x.id);

	const renderForm = () => {
		switch (currentForm) {
			case FORMS.AllLocations.Id:
				return (
					<AllLocationsForm
						setSelectedLocations={setSelectedLocations}
						baselineId={baselineId}
						projectId={formProjectId}
						baselineTaskIds={baselineTaskIds}
						isProjectsActive={isProjectsActive}
						isProjectIdRequiredForLocationTask={isProjectIdRequiredForLocationTask}
					/>
				);
			case FORMS.LocationName.Id:
				return (
					<LocationNameForm
						setSelectedLocations={setSelectedLocations}
						projectId={formProjectId}
						selectedLocations={selectedLocations}
						baselineId={baselineId}
						baselineTaskIds={baselineTaskIds}
						isProjectsActive={isProjectsActive}
						isProjectIdRequiredForLocationTask={isProjectIdRequiredForLocationTask}
					/>
				);
			case FORMS.LocationAttributes.Id:
				return (
					<LocationAttributesForm
						setSelectedLocations={setSelectedLocations}
						selectedLocations={selectedLocations}
						baselineId={baselineId}
						projectId={formProjectId}
						baselineTaskIds={baselineTaskIds}
						isProjectsActive={isProjectsActive}
						isProjectIdRequiredForLocationTask={isProjectIdRequiredForLocationTask}
					/>
				);
			default:
				Notification('error', 'Error', 'Unknown Form Selected');
				return null;
		}
	};

	const menuOptionClick = useCallback((e: any) => {
		// Switching forms always clears the selection; the All Locations form repopulates it
		// from its own query once that form mounts.
		setSelectedLocations([]);
		setCurrentForm(e.item.value);
	}, []);

	const onCustomizeClick = useCallback((e: React.SyntheticEvent) => {
		menuRef.current?.toggle(e);
	}, []);

	const onProjectChange = useCallback((value: string) => {
		setFormProjectId(value.toString());
	}, []);

	const customFooter = (
		<div className="button-wrap text-end">
			<span>Push task(s) to {selectedLocations.length} locations</span>
			<button type="button" className="btn btn-secondary" onClick={onCancel}>Cancel</button>
			<button type="button" className="btn btn-primary button--save" onClick={onPushdown}>Pushdown</button>
		</div>
	);

	const menuOptions = [
		{ label: FORMS.AllLocations.Name, value: FORMS.AllLocations.Id, command: menuOptionClick },
		{ label: FORMS.LocationName.Name, value: FORMS.LocationName.Id, command: menuOptionClick },
		{ label: FORMS.LocationAttributes.Name, value: FORMS.LocationAttributes.Id, command: menuOptionClick },
	];

	/**
	 * Do not include the current form menu item in the context menu that shows
	 * when clicking the customize link
	 */
	const customizeMenuOptions = menuOptions.filter((x) => x.value !== currentForm);

	const currentFormMessage = Object.values(FORMS).find((f) => f.Id === currentForm)?.Message ?? '';

	return (
		<div className="bulk-pushdown-drawer">
			<Menu model={customizeMenuOptions} popup ref={menuRef} />
			<DrawerV6 isWide={true} title="Bulk Pushdown" onCancelClick={onCancel} customFooter={customFooter}>
				<LoadingIndicator isLoading={isLoadingFilters || pushdownMutation.isPending} hideWhileLoading={true}>
					<form noValidate className={isFormValid ? '' : 'was-validated'}>
						{isProjectsActive === true && (
							<div className="row">
								<div className="col">
									<MultiSelectBox
										name="projectId"
										label={isProjectIdRequiredForLocationTask === true ? 'Assign Tasks to Project' : 'Assign Tasks to Project (optional)'}
										idField="id"
										valueField="name"
										options={projects}
										selectedValues={formProjectId.toString()}
										onChange={onProjectChange}
										isRequired={isProjectIdRequiredForLocationTask === true}
										errorMessage="Project is required"
										isMulti={false}
									/>
								</div>
							</div>
						)}

						<div className="row">
							<div className="col drawer-message">
								{currentFormMessage}
								<button type="button" className="btn btn-link" onClick={onCustomizeClick}>Customize</button>
							</div>
						</div>

						<div className="row">
							<div className="col">
								{renderForm()}
							</div>
						</div>
					</form>
				</LoadingIndicator>
			</DrawerV6>
		</div>
	);
};

const BulkPushdownDrawer: React.FC<ComponentProps> = (props) => {
	return (
		<ErrorBoundary FallbackComponent={ErrorFallback} onReset={() => window.location.reload()}>
			<Suspense fallback={<LoadingComponent />}>
				<BulkPushdownDrawerContent {...props} />
			</Suspense>
		</ErrorBoundary>
	);
};

export default BulkPushdownDrawer;
